import { formatPct } from '@/lib/format'
import type { PositionDriftItem } from '@/types/rebalance'
import { DriftBadge } from './DriftBadge'

interface PositionDriftSectionProps {
  drifts: PositionDriftItem[]
}

export function PositionDriftSection({ drifts }: PositionDriftSectionProps) {
  return (
    <section className="hairline mb-6 flex flex-col gap-3 border bg-panel p-4 md:p-6">
      <header className="flex items-baseline justify-between">
        <h2 className="tracked-caps text-xs text-muted">Position-Drift</h2>
        <span className="text-xs text-muted">({drifts.length})</span>
      </header>

      {drifts.some((d) => d.also_in_cluster_drift !== null) && (
        <div className="hairline border border-bernstein/30 bg-bernstein/5 px-4 py-2 text-[11px] text-bernstein-dim">
          ⚠ {drifts.filter((d) => d.also_in_cluster_drift !== null).length} Positionen liegen in
          Clustern, die ebenfalls Drift haben. Doppel-Aktion vermeiden.
        </div>
      )}

      {/* Desktop: table — visible from md: */}
      <table className="hidden w-full text-xs md:table">
        <thead className="bg-void/50 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">Position</th>
            <th className="px-3 py-2 text-right">Target</th>
            <th className="px-3 py-2 text-right">Actual</th>
            <th className="px-3 py-2 text-right">Drift</th>
            <th className="px-3 py-2 text-left">Vorschlag</th>
            <th className="px-3 py-2 text-right">Severity</th>
          </tr>
        </thead>
        <tbody>
          {drifts.map((d) => (
            <tr key={d.position_id} className="hairline-t border-t">
              <td className="px-3 py-2 text-ink">
                <span className="font-mono">{d.ticker}</span>
                <span className="ml-2 text-muted">{d.name}</span>
              </td>
              <td className="px-3 py-2 text-right text-ink">{formatPct(d.target_pct, 1)}</td>
              <td className="px-3 py-2 text-right text-ink">{formatPct(d.actual_pct, 1)}</td>
              <td className="px-3 py-2 text-right text-ink">{formatPct(d.drift_pct, 1)}</td>
              <td className="px-3 py-2 text-ink">{d.suggested_action}</td>
              <td className="px-3 py-2 text-right">
                <DriftBadge severity={d.severity} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile: card-per-row — visible below md: */}
      <ul className="flex flex-col gap-3 md:hidden">
        {drifts.map((d) => (
          <li key={d.position_id} className="hairline border bg-void/30 p-3 text-xs">
            <div className="flex items-baseline justify-between">
              <div>
                <span className="font-mono text-sm text-ink">{d.ticker}</span>
                <span className="ml-2 text-muted">{d.name}</span>
              </div>
              <DriftBadge severity={d.severity} />
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-muted">
              <dt>Target</dt>
              <dd className="text-right text-ink">{formatPct(d.target_pct, 1)}</dd>
              <dt>Actual</dt>
              <dd className="text-right text-ink">{formatPct(d.actual_pct, 1)}</dd>
              <dt>Drift</dt>
              <dd className="text-right text-ink">{formatPct(d.drift_pct, 1)}</dd>
              <dt className="col-span-2">Vorschlag</dt>
              <dd className="col-span-2 text-ink">{d.suggested_action}</dd>
            </dl>
          </li>
        ))}
      </ul>
    </section>
  )
}
